import React, { useMemo } from 'react';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ToastService, useDataTable } from '@iamsoftware/react-hooks';
import { useNavigate } from 'react-router-dom';
import { AgreementService } from '../../../../../service/AgreementService';

interface InProgressTabProps {
    handleSign: (item: any) => void;
    handleSendEmail: (item: any) => void;
    handleCancel: (item: any) => void;
    handleConvertToDraft: (item: any) => void;
    loadCanvas: (file: any) => void;
    setDisplayUploadAttachmentDialog: (data: { agreementId: string, agreementName: string } | null) => void;
    handleShowFiles: (item: any) => void;
    isSigning: boolean;
    sendingEmail: boolean;
    headerResult?: any;
    setReload?: (reloadFn: () => void) => void;
    handleBulkSign?: (items?: any[]) => void;
}

export const InProgressTab: React.FC<InProgressTabProps> = ({
    handleSign,
    handleSendEmail,
    handleCancel,
    handleConvertToDraft,
    loadCanvas,
    setDisplayUploadAttachmentDialog,
    handleShowFiles,
    isSigning,
    sendingEmail,
    headerResult,
    setReload,
    handleBulkSign
}) => {
    const navigate = useNavigate();
    const [isBulkMode, setIsBulkMode] = React.useState(false);
    const columns: any[] = [
        ...(isBulkMode ? [{ selectionMode: 'multiple', width: 50 }] : []),
        {
            field: 'agreementName',
            header: 'Tên tài liệu',
            width: 100,
            matchMode: 'contains',
            dataType: 'custom' as const,
            customCell(row: any) {
                return (
                    <span 
                        className="underline text-primary cursor-pointer" 
                        onClick={() => navigate(`/quan-ly-tai-lieu/tai-lieu/${row.agreementId}?mode=view`)} 
                        title="Xem chi tiết"
                    >
                        {row.agreementName}
                    </span>
                );
            }
        },
        { field: 'agreementNo', header: 'Mã tài liệu', width: 140, matchMode: 'contains' },
        { field: 'agreementTypeName', header: 'Loại tài liệu', width: 160, matchMode: 'contains' },
        // {
        //     field: 'statusDescription',
        //     header: 'Trạng thái',
        //     width: 200,
        //     dataType: 'custom' as const,
        //     customCell(row: any) {
        //         const map: any = {
        //             'AGT_SIGNING_PROCESS': { severity: 'warning', label: 'Đang xử lý ' },
        //             'AGT_SIGNING': { severity: 'warning', label: 'Đang ký' },
        //             'AGT_SIGN_COMPLETED': { severity: 'success', label: 'Hoàn thành' },
        //             'AGT_CANCELLED': { severity: 'danger', label: 'Đã hủy' }
        //         };
        //         const s = map[row.statusId] || { severity: 'secondary', label: row.statusDescription || 'N/A' };
        //         const isUrgent = row.isUrgent === 'Y';
        //         const isImportant = row.isImportant === 'Y';
        //         return (
        //             <div className="flex align-items-center gap-2 flex-wrap">
        //                 <Tag value={s.label} severity={s.severity as any} />
        //                 {isUrgent && (
        //                     <span className="flex align-items-center gap-1 text-orange-500 text-sm" title="Tài liệu cần xử lý gấp">
        //                         <i className="pi pi-bolt"></i>
        //                         <span>Khẩn cấp</span>
        //                     </span>
        //                 )}
        //                 {isImportant && (
        //                     <span className="flex align-items-center gap-1 text-indigo-500 text-sm" title="Tài liệu quan trọng">
        //                         <i className="pi pi-bookmark"></i>
        //                         <span>Quan trọng</span>
        //                     </span>
        //                 )}
        //             </div>
        //         );
        //     }
        // },
        // {
        //     field: 'mainContent',
        //     header: 'File ký',
        //     width: 220,
        //     dataType: 'custom' as const,
        //     customCell(row: any) {
        //         if (!row.mainContent) return <span className="text-gray-500">Chưa có file</span>;
        //         return (
        //             <div className="flex align-items-center gap-2">
        //                 <i className={`pi ${row.mainContent.contentType === 'application/pdf' ? 'pi-file-pdf' : 'pi-file-word'} text-xl`}></i>
        //                 <div>
        //                     <div className="font-medium">{row.mainContent.fileName}</div>
        //                     <small className="text-gray-500">{row.mainContent.contentType?.split('/')?.[1]?.toUpperCase()}</small>
        //                 </div>
        //             </div>
        //         );
        //     }
        // },
        {
            field: 'contentCount',
            header: 'Số file',
            width: 110,
            dataType: 'custom' as const,
            customCell(rowData: any): any {
                const count = rowData.contentCount || 0;
                return (
                    <Tag
                        value={`${count} file`}
                        severity="info"
                        style={{ cursor: count > 0 ? 'pointer' : 'default' }}
                        onClick={() => count > 0 && handleShowFiles(rowData)}
                        title={count > 0 ? 'Click để xem danh sách file' : ''}
                    />
                );
            }
        },
        {
            field: 'expiryDate',
            header: 'Thời gian',
            width: 180,
            dataType: 'custom',
            customCell(rowData: any): any {
                const formatLocal = (value?: any) => {
                    if (!value) return null;
                    const d = new Date(value);
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const HH = String(d.getHours()).padStart(2, '0');
                    const MM = String(d.getMinutes()).padStart(2, '0');
                    const SS = String(d.getSeconds()).padStart(2, '0');
                    return `${dd}/${mm}/${yyyy} ${HH}:${MM}:${SS}`;
                };

                const expiryText = rowData.expiryDate ? formatLocal(rowData.expiryDate) : 'Không có thời hạn ký';
                const updatedText = rowData.lastUpdatedStamp ? formatLocal(rowData.lastUpdatedStamp) : null;

                return (
                    <div className="text-sm">
                        <div className="flex align-items-center gap-2 mb-1">
                            <i className="pi pi-hourglass" />
                            <span>{expiryText}</span>
                        </div>
                        {updatedText && (
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-clock" />
                                <span>{updatedText}</span>
                            </div>
                        )}
                    </div>
                );
            }
        }
    ];

    const mapListResult = (result: any) => {
        if (result.listData && Array.isArray(result.listData)) {
            result.listData = result.listData.map((item: any) => ({
                ...item,
                mainContent: item.mainFileName || item.mainContentType ? {
                    fileName: item.mainFileName,
                    contentType: item.mainContentType,
                    contentLocation: item.referenceContentLocation || item.mainContentLocation,
                    fileId: item.fileId || item.mainFileId || item.mainContentId,
                    googleFileId: item.fileId || item.mainFileId || item.mainContentId,
                    referenceFileId: item.referenceContentLocation
                } : null
            }));
        }
        const total = result.totalRecords ?? (result.listData || []).length;
        return {
            listData: result.listData || [],
            totalRecords: total,
            totalCount: total
        };
    };

    const { render: renderInProgress, reloadLazyData: reloadInProgress, selectedItems } = useDataTable({
        tableHeader: (
            <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full">
                {headerResult || <span>Tài liệu đang xử lý</span>}
                <div className="flex gap-2">
                    {!isBulkMode ? (
                        <Button 
                            label="Ký hàng loạt" 
                            icon="pi pi-user-edit" 
                            className="p-button-success p-button-sm" 
                            onClick={() => setIsBulkMode(true)}
                        />
                    ) : (
                        <>
                            <Button 
                                label="Xác nhận" 
                                icon="pi pi-check" 
                                className="p-button-success p-button-sm" 
                                onClick={() => {
                                    handleBulkSign?.(selectedItems);
                                    setIsBulkMode(false);
                                }}
                            />
                            <Button 
                                label="Bỏ qua" 
                                icon="pi pi-times" 
                                className="p-button-secondary p-button-sm" 
                                onClick={() => setIsBulkMode(false)}
                            />
                        </>
                    )}
                </div>
            </div>
        ),
        selectionMode: isBulkMode ? 'multiple' : undefined,
        dataKey: 'agreementId',
        indexColumnWidth: 45,
        actionColumnWidth: 170,
        columns: columns,
        getList: lazyLoadEvent => {
            const statuses = JSON.stringify(['AGT_SIGNING_PROCESS', 'AGT_CREATED', 'AGT_SIGNING']);
            return AgreementService.getAgreementList(lazyLoadEvent, statuses as any).then(mapListResult);
        },
        initActions(items: Array<any>) {
            items?.forEach(item => {
                const mainFileId = item?.mainContent?.fileId || item?.fileId;
                const hasMainFileUrl = item?.mainContent?.contentLocation;
                const actions: any[] = [];

                
                actions.push(
                    { icon: 'pi pi-user-edit', title: 'Ký', className: 'p-button-success', command: () => { if (!isSigning) handleSign(item); } }
                );
                actions.push(
                    { icon: 'pi pi-send', title: 'Gửi email', className: 'p-button-help', command: () => { if (!sendingEmail) handleSendEmail(item); } }
                );
                actions.push({
                    icon: 'pi pi-upload',
                    title: 'Cập nhật file đính kèm',
                    className: 'p-button-info',
                    command: () => {
                        setDisplayUploadAttachmentDialog({ agreementId: item.agreementId, agreementName: item.agreementName });
                    }
                });
                
                item.actions = actions;

                const actionMenus: any[] = [];
                actionMenus.push(
                    (mainFileId || hasMainFileUrl)
                        ? {
                            icon: 'pi pi-external-link',
                            title: 'Xem file',
                            className: 'p-button-help',
                            label: 'Xem file pdf',
                            command: () => {
                                if (item.mainContent) {
                                    loadCanvas(item.mainContent);
                                } else {
                                    // Fallback
                                    loadCanvas({ 
                                        fileId: mainFileId, 
                                        fileName: item.agreementName, 
                                        contentLocation: hasMainFileUrl 
                                    });
                                }
                            }
                        }
                        : {
                            icon: 'pi pi-eye',
                            title: 'Xem chi tiết',
                            className: 'p-button-info',
                            command: () => navigate(`/quan-ly-tai-lieu/tai-lieu/${item.agreementId}?mode=view`)
                        }
                );
                if (item.partyCount > 0) {
                    actionMenus.push({ icon: 'pi pi-times', label: 'Hủy', command: () => { if (!isSigning) handleCancel(item); } });
                } else {
                    actionMenus.push({ icon: 'pi pi-undo', label: 'Chuyển về nháp', command: () => { if (!isSigning) handleConvertToDraft(item); } });
                }
                item.actionMenus = actionMenus;
            });
        },
        originalProps: { scrollHeight: 'calc(100vh - 300px)' }
    });

    React.useEffect(() => {
        if (setReload) setReload(reloadInProgress);
    }, [reloadInProgress, setReload]);

    return (
        <div className="tai-lieu-ky-table">
            {renderInProgress()}
        </div>
    );
};
